export type ConnectorType = 'shopify' | 'woocommerce' | 'prestashop' | 'magento' | 'drupal' | 'simple_db';
export type IntegrationNodeType = 'trigger' | 'source' | 'mapping' | 'destination';
export type FlowType = 'create_and_update' | 'update_only';

export const FLOW_TYPE_LABELS: Record<FlowType, string> = {
  create_and_update: 'Create and update products',
  update_only: 'Update products',
};

export const FLOW_TYPE_LABEL_KEYS: Record<FlowType, string> = {
  create_and_update: 'PAGES.API_INTEGRATION.FLOW_TYPES.CREATE_AND_UPDATE',
  update_only: 'PAGES.API_INTEGRATION.FLOW_TYPES.UPDATE_ONLY',
};

export interface IntegrationCanvasNode {
  id: string;
  flowId: string;
  type: IntegrationNodeType;
  connectorType?: ConnectorType;
  title: string;
  subtitle?: string;
  x: number;
  y: number;
  config: TriggerConfig | ConnectorConfig | MappingConfig | DestinationConfig | null;
}

export interface IntegrationFlow {
  id: string;
  name: string;
  flowType: FlowType;
  nodes: IntegrationCanvasNode[];
}

// Connector configs (discriminated union)
export interface ShopifyConfig {
  connectorType: 'shopify';
  storeUrl: string;
  apiKey: string;
  apiSecret: string;
}

export interface WooCommerceConfig {
  connectorType: 'woocommerce';
  storeUrl: string;
  consumerKey: string;
  consumerSecret: string;
}

export interface PrestaShopConfig {
  connectorType: 'prestashop';
  storeUrl: string;
  apiKey: string;
}

export interface MagentoConfig {
  connectorType: 'magento';
  storeUrl: string;
  accessToken: string;
}

export interface DrupalConfig {
  connectorType: 'drupal';
  siteUrl: string;
  apiEndpoint: string;
  authToken: string;
}

export interface SimpleDBConfig {
  connectorType: 'simple_db';
  connectionString: string;
  tableOrCollection: string;
  query: string;
}

export type ConnectorConfig =
  | ShopifyConfig
  | WooCommerceConfig
  | PrestaShopConfig
  | MagentoConfig
  | DrupalConfig
  | SimpleDBConfig;

export interface FieldMapping {
  id: string;
  sourceField: string;
  targetField: string;
}

export interface MappingConfig {
  mappings: FieldMapping[];
  autoMapped: boolean;
}

export type SyncFrequency = 'hourly' | 'every_6h' | 'daily' | 'weekly';

export interface TriggerConfig {
  syncMode: 'manual' | 'scheduled' | 'realtime';
  frequency?: SyncFrequency;
}

export interface DestinationConfig {
  matchField: 'sku' | 'name';
  syncFields?: string[];
  syncBehavior?: 'create_and_update' | 'update_only';
  /**
   * Default product family slug applied to products CREATED by this flow
   * (create_and_update mode only). Lets the V3 editor open the draft without
   * the vendor needing to pick a family first. Vendor can still change it
   * per-product before publishing.
   */
  defaultFamilySlug?: string;
}

export interface ProductSyncDetail {
  sku?: string;
  name?: string;
  outcome: 'created' | 'updated' | 'skipped' | 'failed';
  error?: string;
  matchField?: string;
  matchValue?: string;
}

export interface SyncJobStatus {
  _id: string;
  status: 'pending' | 'running' | 'completed' | 'failed';
  trigger: 'manual' | 'scheduled' | 'webhook';
  totalProducts: number;
  processedProducts: number;
  createdProducts: number;
  updatedProducts: number;
  failedProducts: number;
  skippedProducts?: number;
  errors: string[];
  productDetails?: ProductSyncDetail[];
  createdAt: string;
  completedAt?: string;
}

export type NodeExecutionStatus = 'idle' | 'running' | 'completed' | 'failed';

export interface NodeExecutionResult {
  summary: string;       // inline badge text e.g. "10 products"
  details?: {            // detailed info for drawer
    label: string;
    value: string | number;
  }[];
  errors?: string[];
  productDetails?: ProductSyncDetail[];
}

export interface FlowExecutionState {
  status: 'idle' | 'running' | 'completed' | 'failed';
  nodeStatuses: Record<string, NodeExecutionStatus>;
  nodeResults: Record<string, NodeExecutionResult>;
  syncStatus: SyncJobStatus | null;
}

export interface ConnectorConfigField {
  key: string;
  labelKey: string;
  type: 'text' | 'password' | 'textarea';
  placeholder?: string;
  helperTextKey?: string;
}

export interface ConnectorCatalogEntry {
  type: ConnectorType;
  nameKey: string;
  category: 'ecommerce' | 'other';
  color: string;
  configFields: ConnectorConfigField[];
}
